import {ISSUE_VIEWER_CACHE_TTL_MS} from '@github-ui/preheat-issues-manager/issue-viewer-storage-key'
import {getQueryClient} from '@github-ui/query-client'
import {queryOptions} from '@github-ui/react-query'
import {createServerPersistedQueryPersister, removePersistedQueryData} from '@github-ui/react-query-persister'
import {IssueViewerSecondaryViewServerQuery} from '@github-ui/server-persisted-queries'
import type {IssueViewerSecondaryViewServerQueryQuery} from '@github-ui/server-persisted-queries/types/IssueViewerSecondaryViewServerQuery'
import type {SendAnalyticsEventFunction} from '@github-ui/use-analytics'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'
import {versionMismatchDetector} from '@github-ui/version-mismatch-detector'

export type IssueViewerSecondaryViewFeatures = {
  readonly hideClosedSubIssues?: boolean
}

export type IssueViewerSecondaryViewResponse = {
  readonly repository: IssueViewerSecondaryViewServerQueryQuery['repository']
  readonly features?: IssueViewerSecondaryViewFeatures
}

export function issueViewerSecondaryViewQueryKey(owner: string, repo: string, number: number) {
  // Deliberately excludes `markAsRead` so toggling it doesn't bust/duplicate the cache entry,
  // mirroring the Relay path's `excludeFromCacheKey: ['markAsRead']`.
  return ['issue-viewer', 'secondary-view', {owner, repo, number}] as const
}

type IssueViewerSecondaryViewQueryOptionsProps = {
  owner: string
  repo: string
  number: number
  markAsRead: boolean
  sendAnalyticsEvent: SendAnalyticsEventFunction
  preheat?: boolean
  /** Sent as `X-GITHUB-PREHEAT` on preheat fetches so the server can tag them by surface. */
  preheatSource?: string
}

export function issueViewerSecondaryViewQueryOptions({
  owner,
  repo,
  number,
  markAsRead,
  sendAnalyticsEvent,
  preheat = false,
  preheatSource,
}: IssueViewerSecondaryViewQueryOptionsProps) {
  // eslint-disable-next-line @tanstack/query/exhaustive-deps -- markAsRead and preheatSource change the request without identifying the cached resource
  return queryOptions({
    queryKey: issueViewerSecondaryViewQueryKey(owner, repo, number),
    queryFn: ({signal}) =>
      fetchIssueViewerSecondaryView(owner, repo, number, markAsRead, signal, preheat ? preheatSource : undefined),
    staleTime: 0,
    gcTime: ISSUE_VIEWER_CACHE_TTL_MS,
    meta: preheat ? {isPreload: true} : undefined,
    persister: createServerPersistedQueryPersister({
      queryHash: IssueViewerSecondaryViewServerQuery,
      sendAnalyticsEvent,
      maxAgeMs: ISSUE_VIEWER_CACHE_TTL_MS,
      alwaysRefetch: true,
    }),
  })
}

export async function removePersistedIssueViewerSecondary(
  {owner, repo, number}: {owner: string; repo: string; number: number},
  queryClient: ReturnType<typeof getQueryClient> = getQueryClient(),
): Promise<void> {
  const options = issueViewerSecondaryViewQueryOptions({
    owner,
    repo,
    number,
    markAsRead: false,
    sendAnalyticsEvent: () => {},
  })
  await removePersistedQueryData(options.persister, options.queryKey, queryClient)
}

export async function fetchIssueViewerSecondaryView(
  owner: string,
  repo: string,
  number: number,
  markAsRead: boolean,
  signal?: AbortSignal,
  preheatSource?: string,
): Promise<IssueViewerSecondaryViewResponse> {
  const response = await verifiedFetchJSON(
    `/${owner}/${repo}/issues/${number}/secondary_view?markAsRead=${markAsRead}&queryId=${IssueViewerSecondaryViewServerQuery}`,
    {signal, credentials: 'include', headers: preheatSource ? {'X-GITHUB-PREHEAT': preheatSource} : undefined},
  )
  versionMismatchDetector.checkResponse(response)
  if (!response.ok) {
    throw new Error(`Issue viewer secondary view fetch failed (${response.status})`)
  }

  const json = (await response.json()) as {
    data?: IssueViewerSecondaryViewServerQueryQuery
    errors?: unknown[]
    features?: IssueViewerSecondaryViewFeatures
  }
  if (!json.data) {
    throw new Error('Issue viewer secondary view query returned no data')
  }

  return {
    repository: json.data.repository,
    features: json.features,
  }
}
